import React, { createContext, useCallback, useContext, useState } from 'react';
import { CreateEventRegistration, EventRegistrationSelection } from '../generated/api/dsv-public/model';
import { FormType } from '../shared/FormController';
import { BillingInfo, RegistrantInfo } from '../forms/form.types';

type FormCheckpointContextProps = {
  savedActiveForm?: FormType;
  savedRegistrant?: RegistrantInfo;
  savedRegistrationSelection?: EventRegistrationSelection;
  savedRegistrationEntries?: CreateEventRegistration | CreateEventRegistration[];
  savedBillingInfo?: BillingInfo;
  setCheckpoint: (
    activeForm: FormType,
    registrant: RegistrantInfo,
    registrationSelection: EventRegistrationSelection,
    registrationEntries: CreateEventRegistration | CreateEventRegistration[],
    billingInfo?: BillingInfo
  ) => void;
};

export const FormCheckpointContext = createContext<FormCheckpointContextProps>({} as FormCheckpointContextProps);

type FormCheckpointProviderProps = {
  children: React.ReactNode;
};

export const FormCheckpointProvider: React.FC<FormCheckpointProviderProps> = ({ children }) => {
  const [activeForm, setActiveForm] = useState<FormType>();
  const [registrant, setRegistrant] = useState<RegistrantInfo>();
  const [registrationSelection, setRegistrationSelection] = useState<EventRegistrationSelection>();
  const [registrationEntries, setRegistrationEntries] = useState<CreateEventRegistration | CreateEventRegistration[]>();
  const [billingInfo, setBillingInfo] = useState<BillingInfo>();

  const setCheckpoint = useCallback(
    (
      activeForm: FormType | undefined,
      registrant: RegistrantInfo | undefined,
      registrationSelection: EventRegistrationSelection | undefined,
      registrationEntries: CreateEventRegistration | CreateEventRegistration[] | undefined,
      billingInfo?: BillingInfo
    ) => {
      setActiveForm(activeForm);
      setRegistrant(registrant);
      setRegistrationSelection(registrationSelection);
      setRegistrationEntries(registrationEntries);
      setBillingInfo(billingInfo);
    },
    [setActiveForm, setRegistrant, setRegistrationSelection, setRegistrationEntries]
  );

  return (
    <FormCheckpointContext.Provider
      value={{
        savedActiveForm: activeForm,
        savedRegistrant: registrant,
        savedRegistrationSelection: registrationSelection,
        savedRegistrationEntries: registrationEntries,
        savedBillingInfo: billingInfo,
        setCheckpoint: setCheckpoint,
      }}
    >
      {children}
    </FormCheckpointContext.Provider>
  );
};

export const useFormCheckpoint = (): FormCheckpointContextProps => useContext(FormCheckpointContext);
